import { PaletteColor } from "@brexhq/metal-foundation";
import styled from "@emotion/styled";
import { AnimatePresence, m } from "framer-motion";
import React from "react";

import { IconPicker } from "@brexhq/next/src/components/building-blocks/IconPicker/IconPicker";
import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import {
  getModuleThemeColors,
  ModuleLayout,
} from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import BlockRenderer from "@brexhq/next/src/components/shared/BlockRenderer";
import { bannerExperiment } from "@brexhq/next/src/config/experimentation/data";
import { allBanners } from "@brexhq/next/src/data/siteSettings";
import {
  ExperimentAnalyticsData,
  useTrackStatsigIfExperiment,
} from "@brexhq/next/src/hooks/analytics/useTrackStatsig";
import useIsomorphicLayoutEffect from "@brexhq/next/src/hooks/useIsomorphicLayoutEffect";
import { statsigClientSide } from "@brexhq/next/src/utils/experimentation/statsig";
import { TextColor } from "@brexhq/shared/components/Typography/types";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import {
  Icon as IconType,
  IconColors,
  IconSizes,
} from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/IconPicker";
import { PageType } from "@brexhq/shared/utils/sanity/pageTypes";

const IconWrapper = styled(Container)<{ iconColor: PaletteColor }>(
  ({ theme, iconColor }) => ({
    marginRight: theme.spacing.LRG.xxsml,
    color: `${iconColor} !important`,
    //TODO: we need to make this not like this - but for now it will have to do. needed to override the icon / text color for SVB stuff
    ["& *"]: {
      color: `${iconColor} !important`,
    },
  }),
);

export interface Banner {
  _id: string;
  _updatedAt: string;
  theme: ModuleThemeType;
  icon?: IconType;
  iconColor: IconColors;
  color?: IconColors;
  textColor: TextColor;
  size: IconSizes;
  bannerBlockContentRaw: any;
  optOutPages: string[];
  optOutPageTypes: string[];
}

interface Props {
  banner: Banner;
  currentPageSlug?: string;
  currentPageType?: PageType;
  hidden?: boolean;

  // Used for Sanity Preview & Storybook only
  forceOpen?: boolean;
}

// This is a temporary fix to remove the underline from the link in the banner
// TODO: Remove this once we have a proper way to override the link decoration in the banner
const AnnouncementContentContainer = styled(Container)({
  "& a": {
    textDecoration: "none !important",
  },
});

export const AnnouncementBanner: React.FC<Props> = ({
  banner,
  currentPageSlug,
  currentPageType,
  hidden,
  forceOpen,
}) => {
  const [bannerData, setBannerData] = React.useState<Banner>(banner);

  // If we have a banner experiment, we need to wait for the experiment to load before we can show the banner
  const [loading, setLoading] = React.useState(Boolean(bannerExperiment));
  const [analyticsData, setAnalyticsData] =
    React.useState<ExperimentAnalyticsData | null>(null);

  useIsomorphicLayoutEffect(() => {
    const getBannerExperiment = async () => {
      if (!bannerExperiment) return;

      const {
        statsigExperimentId,
        references,
        parameterKey,
        defaultParameterValue,
        experimentStatus,
      } = bannerExperiment;
      const { value } = await statsigClientSide({
        statsigExperimentId,
        parameterKey,
        defaultParameterValue,
      });

      const banner = references[value];

      setLoading(false);
      setBannerData(allBanners[banner.docId] as Banner);
      setAnalyticsData({
        experimentId: statsigExperimentId,
        experimentType: "banner",
        variation: banner.variation,
        experimentStatus,
      });
    };

    getBannerExperiment();
  }, []);

  // Will only track if inside an experiment
  useTrackStatsigIfExperiment(analyticsData);

  const {
    theme,
    icon,
    textColor,
    iconColor,
    color,
    bannerBlockContentRaw,
    optOutPages,
    optOutPageTypes,
    size = "sml",
  } = bannerData;

  const { standard } = getModuleThemeColors(theme);

  const overWrittenColor = standard[textColor] as PaletteColor;

  // Theres not a mechanism to override the color choosed from the them for the BlockRenderer, so i need to "make" a new theme here using the color the user has picked in sanity
  const modifiedStandardTheme = {
    ...standard,
    textPrimary: overWrittenColor,
    textSecondary: overWrittenColor,
  };

  const pageIsOptedOut =
    (currentPageSlug && optOutPages.includes(currentPageSlug)) ||
    (currentPageType && optOutPageTypes.includes(currentPageType));

  const shouldShowBanner =
    (!hidden && !pageIsOptedOut && !loading) || forceOpen;

  const resolvedIconColor = color ?? iconColor;

  return (
    <AnimatePresence initial={false}>
      {shouldShowBanner && (
        <m.div
          key="banner"
          initial="closed"
          animate="open"
          exit="closed"
          variants={{
            open: {
              height: "auto",
              opacity: 1,
            },
            closed: {
              height: 0,
              opacity: 0,
            },
          }}
          transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
        >
          <ModuleLayout
            theme={theme}
            topPadding={"xsml"}
            bottomPadding={"xsml"}
            topSpacing={"none"}
          >
            <Container
              display="flex"
              textColor={standard[textColor]}
              justifyContent="center"
              padding={["none", "xsml", "none", "sml"]}
            >
              <AnnouncementContentContainer
                display="flex"
                justifyContent="center"
                alignItems="center"
                flex={1}
                textAlign="center"
                padding={["none", "med"]}
              >
                {icon && iconColor && (
                  <IconWrapper
                    iconColor={overWrittenColor}
                    display={{ SML: "none" }}
                  >
                    <IconPicker
                      icon={icon}
                      color={resolvedIconColor}
                      theme={theme}
                      size={size}
                      marginTop="none"
                    />
                  </IconWrapper>
                )}
                <BlockRenderer
                  blocks={bannerBlockContentRaw}
                  theme={modifiedStandardTheme}
                  typeScale="S"
                  component="announcement-banner"
                />
              </AnnouncementContentContainer>
            </Container>
          </ModuleLayout>
        </m.div>
      )}
    </AnimatePresence>
  );
};

/* Used for Sanity previews */
export const AnnouncementBannerPreview = ({ data }: { data: Banner }) => (
  <AnnouncementBanner banner={data} forceOpen />
);
